import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import "./AgentDetailsComp.scss";
import arrowIcon from "../../../assets/Icons/map-tooltip-arrow.svg";

const ROUTE_COLOR = "#F2654A";
const AHEAD_ROUTE_COLOR = "#F9A58F"; // lighter tint of the route colour
const AHEAD_MARKER_COLOR = "#F9A58F";
const SPEEDS = [0.25, 0.5, 1, 2, 4];
const MS_PER_SEGMENT = 1400;
const MIN_DURATION_MS = 10000;
const MAX_DURATION_MS = 180000;
// Street-level zoom playback pulls in to, so the route is readable while it
// plays instead of staying at the whole-route overview.
const PLAYBACK_ZOOM = 16;

// The tracking payload does not guarantee a single timestamp key, so read the
// first one that is actually present on the point.
const getPointTime = (point) =>
  point?.timestamp ||
  point?.created_at ||
  point?.datetime ||
  point?.date_time ||
  point?.location_time ||
  point?.time ||
  point?.updated_at ||
  null;

const formatTime = (value, format) => {
  if (!value) return "--:--";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : String(value);
};

const getPointTimestamp = (point) => {
  const raw = getPointTime(point);
  if (!raw) return null;
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.valueOf() : null;
};

// The tracking API returns the trail newest-first, so the raw order would replay
// the day backwards. Put the points back in the order the agent travelled them.
const toChronologicalOrder = (points) => {
  if (points.length < 2) return points;

  const times = points.map(getPointTimestamp);

  // Every point is timestamped - sort outright, keeping equal times stable.
  if (times.every((time) => time !== null)) {
    return points
      .map((point, index) => ({ point, time: times[index], index }))
      .sort((a, b) => a.time - b.time || a.index - b.index)
      .map((entry) => entry.point);
  }

  // Only some points are timestamped - flip the trail when the ends are clearly
  // newest-first, rather than reordering on incomplete data.
  const firstTimed = times.findIndex((time) => time !== null);
  if (firstTimed === -1) return points;
  const lastTimed =
    times.length - 1 - [...times].reverse().findIndex((time) => time !== null);

  return lastTimed > firstTimed && times[firstTimed] > times[lastTimed]
    ? [...points].reverse()
    : points;
};

const buildPopupContent = (point) => {
  if (!point?.evidence && !point?.summary) return null;

  return `
  <div style="min-width:260px; max-width:320px; background:#fff; border-radius:12px; display:flex; flex-direction:column; gap:2px;">

    <!-- Header -->
    <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
      <span class="map-tooltip-grey-text">Report Summary</span>
      <span class="map-tooltip-grey-text" >Evidence</span>
    </div>

    <!-- Content -->
    <div style="display:flex; align-items:flex-start; gap:12px;">
      <!-- Summary -->
      <div class="map-tooltip-summary">
        ${point?.summary || "<em style='color:#888; font-size:12px;'>No summary</em>"}
      </div>

      <!-- Evidence -->
      <div style="display:flex; ; align-items:start; gap:10px;">
        ${
          point?.evidence
            ? `<div class="map-tooltip-evidence-container">
            <img src='${point.evidence}' alt='evidence' class="map-tooltip-evidence-img"
                 />
            </div>`
            : "<em style='color:#888; font-size:12px;'>No evidence</em>"
        }
        ${
          point?.evidence
            ? `<div class="map-tooltip-arrow-container">
        <img src='${arrowIcon}' alt='arrow' style='width:16px; height:16px; margin-top:4px;' />
        </div>`
            : ""
        }
      </div>
    </div>
  </div>
`;
};

const AgentMapWithPopup = ({
  data,
  data2,
  isSingle = false,
  showTimeline = false,
}) => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const traveledLineRef = useRef(null);
  const agentMarkerRef = useRef(null);
  const markersRef = useRef([]);
  const progressRef = useRef(0);
  const activeIndexRef = useRef(-1);
  const uiProgressRef = useRef(0);
  const lastPanRef = useRef(0);
  const followingRef = useRef(true);

  const [routePoints, setRoutePoints] = useState([]);
  const [singlePoint, setSinglePoint] = useState([]);
  const [timelineMode, setTimelineMode] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [following, setFollowing] = useState(true);
  const { feedback_details } = data2?.data || {};

  const locationData = useMemo(
    () =>
      toChronologicalOrder(
        (data?.data?.location_data || []).filter(
          (item) =>
            item?.latitude != null &&
            item?.longitude != null &&
            !isNaN(Number(item.latitude)) &&
            !isNaN(Number(item.longitude)),
        ),
      ),
    [data],
  );

  useEffect(() => {
    setRoutePoints(
      locationData.map((item) => [
        Number(item.latitude),
        Number(item.longitude),
      ]),
    );
  }, [locationData]);

  useEffect(() => {
    let lat = Number(data2?.data?.feedback_details?.lat);
    let lng = Number(data2?.data?.feedback_details?.long);
    // Fallback to default if invalid
    if (isNaN(lat) || isNaN(lng)) {
      lat = 23.5337;
      lng = 87.3218;
    }
    setSinglePoint(data2?.data?.feedback_details ? [lat, lng] : []);
  }, [data2]);

  const totalSegments = Math.max(routePoints.length - 1, 0);
  const canPlayTimeline = showTimeline && !isSingle && totalSegments > 0;
  const durationMs = useMemo(
    () =>
      Math.min(
        Math.max(totalSegments * MS_PER_SEGMENT, MIN_DURATION_MS),
        MAX_DURATION_MS,
      ),
    [totalSegments],
  );

  // Points carrying a report/evidence are the actual visits, so they become the
  // timeline stops. When none of them do, sample the raw trail instead.
  const stops = useMemo(() => {
    const withContent = locationData
      .map((point, index) => ({ point, index }))
      .filter(({ point }) => point?.evidence || point?.summary);

    if (withContent.length) return withContent;

    const step = Math.max(Math.ceil(locationData.length / 12), 1);
    return locationData
      .map((point, index) => ({ point, index }))
      .filter(({ index }) => index % step === 0);
  }, [locationData]);

  const positionAt = useCallback(
    (value) => {
      if (!routePoints.length) return null;
      const index = Math.floor(value);
      if (index >= routePoints.length - 1)
        return routePoints[routePoints.length - 1];
      const fraction = value - index;
      const [aLat, aLng] = routePoints[index];
      const [bLat, bLng] = routePoints[index + 1];
      return [aLat + (bLat - aLat) * fraction, aLng + (bLng - aLng) * fraction];
    },
    [routePoints],
  );

  const applyProgress = useCallback(
    (value) => {
      const clamped = Math.min(Math.max(value, 0), totalSegments);
      progressRef.current = clamped;

      const map = leafletMapRef.current;
      const current = positionAt(clamped);
      if (!map || !current) return;

      const index = Math.floor(clamped);

      if (traveledLineRef.current) {
        traveledLineRef.current.setLatLngs([
          ...routePoints.slice(0, index + 1),
          current,
        ]);
      }

      if (agentMarkerRef.current) {
        agentMarkerRef.current.setLatLng(current);
        const latLng = L.latLng(current);
        const now = performance.now();
        // Throttled - panning on every frame keeps restarting the animation.
        // Skipped entirely once the viewer takes over the map, so their zoom
        // or pan is not yanked back to the agent.
        if (
          followingRef.current &&
          !map.getBounds().pad(-0.2).contains(latLng) &&
          now - lastPanRef.current > 700
        ) {
          lastPanRef.current = now;
          map.panTo(latLng, { animate: true, duration: 0.5 });
        }
      }

      // Only touch marker styling / popups when the agent reaches a new point.
      if (index !== activeIndexRef.current) {
        activeIndexRef.current = index;

        markersRef.current.forEach((entry) => {
          if (!entry?.marker) return;
          const visited = entry.index <= index;
          entry.marker.setStyle({
            color: entry.hasContent
              ? visited
                ? ROUTE_COLOR
                : AHEAD_MARKER_COLOR
              : "#fff",
            fillColor: entry.hasContent
              ? "#fff"
              : visited
                ? ROUTE_COLOR
                : AHEAD_MARKER_COLOR,
          });
        });

        const reached = markersRef.current.find(
          (entry) => entry.index === index && entry.hasContent,
        );
        if (reached?.marker?.getPopup()) reached.marker.openPopup();
      }

      // Throttle re-renders - the animation loop runs far faster than the UI needs.
      const stepSize = Math.max(totalSegments / 200, 0.005);
      if (
        Math.abs(clamped - uiProgressRef.current) >= stepSize ||
        clamped === 0 ||
        clamped === totalSegments
      ) {
        uiProgressRef.current = clamped;
        setProgress(clamped);
      }
    },
    [positionAt, routePoints, totalSegments],
  );

  useEffect(() => {
    if (!mapRef.current || mapRef.current._leaflet_id) return;

    let detachFollowGuards = () => {};

    const map = L.map(mapRef.current).setView(
      routePoints.length > 0 ? routePoints[0] : [20.5937, 78.9629], // fallback India center
      15,
    );
    leafletMapRef.current = map;
    markersRef.current = [];
    traveledLineRef.current = null;
    agentMarkerRef.current = null;
    activeIndexRef.current = -1;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    if (!isSingle) {
      if (timelineMode) {
        // White casing keeps the route legible over the map tiles.
        L.polyline(routePoints, {
          color: "#fff",
          weight: 7,
          opacity: 0.85,
        }).addTo(map);
        // The whole route is coloured up front, so the path still ahead of the
        // agent is visible from the moment playback starts.
        L.polyline(routePoints, {
          color: AHEAD_ROUTE_COLOR,
          weight: 4,
          opacity: 1,
        }).addTo(map);
        // The travelled portion is painted solid on top as it plays.
        traveledLineRef.current = L.polyline([], {
          color: ROUTE_COLOR,
          weight: 4.5,
          opacity: 1,
        }).addTo(map);
      } else {
        // Draw route line + polygon
        L.polyline(routePoints, {
          color: ROUTE_COLOR,
          weight: 3,
          opacity: 0.7,
        }).addTo(map);
        L.polygon(routePoints, {
          color: ROUTE_COLOR,
          fillColor: ROUTE_COLOR,
          fillOpacity: 0.2,
        }).addTo(map);
      }

      // Add markers dynamically
      locationData.forEach((point, index) => {
        const hasContent = Boolean(point.evidence || point.summary);
        const popupContent = buildPopupContent(point);

        const marker = L.circleMarker(
          [Number(point.latitude), Number(point.longitude)],
          {
            radius: hasContent ? 6 : 3, // Smaller radius if no content
            color: hasContent ? ROUTE_COLOR : "#fff", // Orange border if content, White if not
            fillColor: hasContent ? "#fff" : ROUTE_COLOR, // White fill if content, Orange if not
            fillOpacity: 1,
            weight: 1,
          },
        ).addTo(map);

        if (hasContent && popupContent) {
          marker.bindPopup(popupContent, { closeButton: false });

          marker.on("mouseover", function () {
            this.openPopup();
          });
          marker.on("mouseout", function () {
            this.closePopup();
          });
        }

        markersRef.current.push({ marker, index, hasContent });
      });

      if (timelineMode && routePoints.length) {
        const flagIcon = (label, variant) =>
          L.divIcon({
            className: `fa-timeline-flag ${variant}`,
            html: `<span>${label}</span>`,
            iconSize: [44, 20],
            iconAnchor: [22, 26],
          });

        L.marker(routePoints[0], {
          icon: flagIcon("Start", "is-start"),
        }).addTo(map);
        L.marker(routePoints[routePoints.length - 1], {
          icon: flagIcon("End", "is-end"),
        }).addTo(map);

        agentMarkerRef.current = L.marker(routePoints[0], {
          icon: L.divIcon({
            className: "fa-timeline-agent",
            html: `
              <span class="ripple"></span>
              <span class="ripple is-delayed"></span>
              <svg class="pin" width="32" height="42" viewBox="0 0 32 42" fill="none">
                <path d="M16 41s13-15.1 13-25A13 13 0 1 0 3 16c0 9.9 13 25 13 25z"
                      fill="#F2654A" stroke="#fff" stroke-width="2.5" />
                <circle cx="16" cy="12" r="3.6" fill="#fff" />
                <path d="M9.6 22.6c0-3.5 2.9-6.4 6.4-6.4s6.4 2.9 6.4 6.4z" fill="#fff" />
              </svg>
            `,
            iconSize: [32, 42],
            iconAnchor: [16, 41],
          }),
          zIndexOffset: 1000,
        }).addTo(map);

        map.fitBounds(L.latLngBounds(routePoints), { padding: [40, 40] });
        applyProgress(progressRef.current);

        // Any manual zoom/pan hands control of the viewport to the viewer.
        // mousedown covers the zoom buttons and double-click zoom too.
        const container = map.getContainer();
        const releaseFollow = () => {
          if (!followingRef.current) return;
          followingRef.current = false;
          setFollowing(false);
        };

        container.addEventListener("wheel", releaseFollow, { passive: true });
        container.addEventListener("mousedown", releaseFollow);
        container.addEventListener("touchstart", releaseFollow, {
          passive: true,
        });
        map.on("dragstart", releaseFollow);

        detachFollowGuards = () => {
          container.removeEventListener("wheel", releaseFollow);
          container.removeEventListener("mousedown", releaseFollow);
          container.removeEventListener("touchstart", releaseFollow);
        };
      }
    } else if (isSingle) {
      // Single: show a marker at the dummy location, draw a dotted circle, and focus map on it
      // Fallback to default if singlePoint is empty or invalid
      const validPoint =
        Array.isArray(singlePoint) &&
        singlePoint.length === 2 &&
        !isNaN(singlePoint[0]) &&
        !isNaN(singlePoint[1])
          ? singlePoint
          : [23.5337, 87.3218];
      map.setView(validPoint, 17, { animate: true });
      // Draw dotted circle
      L.circle(validPoint, {
        radius: 120, // meters
        color: ROUTE_COLOR,
        weight: 2,
        fill: true,
        fillColor: ROUTE_COLOR,
        fillOpacity: 0.08,
        dashArray: "6 6",
      }).addTo(map);
      // Draw marker
      L.circleMarker(validPoint, {
        radius: 8,
        color: "#4285F4",
        fillColor: "#fff",
        fillOpacity: 1,
        weight: 3,
      }).addTo(map);
    }

    return () => {
      // Null the ref first so an in-flight frame cannot touch a dying map.
      if (leafletMapRef.current === map) leafletMapRef.current = null;
      detachFollowGuards();

      try {
        // Abort pan/zoom animations still in flight.
        map.stop();
        // A wheel zoom queues _performZoom on a timer that remove() leaves
        // behind - it would fire against detached panes and throw.
        if (map.scrollWheelZoom?._timer) {
          clearTimeout(map.scrollWheelZoom._timer);
          map.scrollWheelZoom._timer = null;
        }
        map.off();
        map.remove();
      } catch (error) {
        // The map was already torn down - nothing left to clean up.
      }

      markersRef.current = [];
      traveledLineRef.current = null;
      agentMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePoints, singlePoint, timelineMode, isSingle]);

  // Playback loop
  useEffect(() => {
    if (!playing || !timelineMode || !totalSegments) return;

    let frame;
    let last = performance.now();

    const tick = (now) => {
      const delta = now - last;
      last = now;
      const next =
        progressRef.current + (delta / durationMs) * totalSegments * speed;

      if (next >= totalSegments) {
        applyProgress(totalSegments);
        setPlaying(false);
        return;
      }

      applyProgress(next);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, timelineMode, speed, durationMs, totalSegments, applyProgress]);

  const resetPlayback = useCallback(() => {
    progressRef.current = 0;
    uiProgressRef.current = 0;
    activeIndexRef.current = -1;
    setProgress(0);
  }, []);

  const toggleTimeline = () => {
    setPlaying(false);
    resetPlayback();
    setTimelineMode((prev) => !prev);
  };

  // Pulls in to street level on the agent. Never zooms out, so a viewer who is
  // already closer in keeps their own zoom.
  const zoomToAgent = () => {
    const map = leafletMapRef.current;
    const current = positionAt(progressRef.current);
    if (!map || !current) return;

    lastPanRef.current = performance.now();
    map.setView(L.latLng(current), Math.max(map.getZoom(), PLAYBACK_ZOOM), {
      animate: true,
    });
  };

  const handlePlayPause = () => {
    const starting = !playing;

    if (starting && progressRef.current >= totalSegments) {
      resetPlayback();
      applyProgress(0);
    }

    // Pull in to street level as playback begins, unless the viewer has already
    // taken the viewport over.
    if (starting && followingRef.current) zoomToAgent();

    setPlaying(starting);
  };

  // Hands the viewport back to the animation and snaps to the agent at once.
  const resumeFollow = () => {
    followingRef.current = true;
    setFollowing(true);
    lastPanRef.current = 0;
    zoomToAgent();
  };

  const handleRestart = () => {
    resetPlayback();
    resumeFollow();
    applyProgress(0);
    setPlaying(true);
  };

  const handleScrub = (event) => {
    applyProgress(Number(event.target.value));
  };

  const currentIndex = Math.max(
    Math.min(Math.floor(progress), locationData.length - 1),
    0,
  );
  const currentPoint = locationData[currentIndex];
  const startTime = getPointTime(locationData[0]);
  const endTime = getPointTime(locationData[locationData.length - 1]);
  const progressPercent = totalSegments ? (progress / totalSegments) * 100 : 0;

  return (
    <div className="fa-map-wrapper">
      {showTimeline && !isSingle && (
        <div className="fa-map-toolbar">
          <p className="fa-map-toolbar-title">Location Tracking</p>
          <button
            type="button"
            className={`fa-timeline-toggle ${timelineMode ? "is-active" : ""}`}
            onClick={toggleTimeline}
            disabled={!canPlayTimeline}
            title={
              canPlayTimeline
                ? "Replay the agent's route"
                : "Not enough location points to replay"
            }
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle
                cx="8"
                cy="8"
                r="6.5"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M8 4.6V8l2.4 1.6"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            {timelineMode ? "Hide Timeline" : "Timeline"}
          </button>
        </div>
      )}

      <div className="fa-map-canvas">
        <div
          ref={mapRef}
          key={JSON.stringify(
            isSingle ? singlePoint : [routePoints, timelineMode],
          )}
          className="fa-map-canvas-inner"
        />
        {isSingle && (
          <img
            src={feedback_details?.image}
            alt="evidence"
            style={{
              position: "absolute",
              right: 5,
              bottom: 5,
              width: 180,
              height: 120,
              objectFit: "cover",
              borderRadius: 10,
              border: "2px solid #fff",
              boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
              background: "#fff",
              zIndex: 199999,
            }}
          />
        )}
      </div>

      {timelineMode && canPlayTimeline && (
        <div className="fa-timeline">
          <div className="fa-timeline-controls">
            <button
              type="button"
              className="fa-timeline-btn is-primary"
              onClick={handlePlayPause}
              title={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <rect
                    x="2"
                    y="1.5"
                    width="3"
                    height="9"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="7"
                    y="1.5"
                    width="3"
                    height="9"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <path d="M3 1.8v8.4L10 6 3 1.8z" fill="currentColor" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="fa-timeline-btn"
              onClick={handleRestart}
              title="Replay from start"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8a5 5 0 108-4l1.8 1.6M12.9 2.6v3.2H9.7"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className={`fa-timeline-btn ${following ? "is-on" : ""}`}
              onClick={resumeFollow}
              disabled={following}
              title={
                following
                  ? "Following the agent - zoom or drag the map to look around freely"
                  : "Re-centre on the agent and resume following"
              }
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle
                  cx="8"
                  cy="8"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="1.4"
                />
                <path
                  d="M8 1v2.2M8 12.8V15M1 8h2.2M12.8 8H15"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <div className="fa-timeline-track">
              <input
                type="range"
                min={0}
                max={totalSegments}
                step="any"
                value={progress}
                onChange={handleScrub}
                style={{ "--fa-progress": `${progressPercent}%` }}
              />
              <div className="fa-timeline-ticks">
                {stops.map((stop) => (
                  <span
                    key={`tick-${stop.index}`}
                    className={stop.index <= currentIndex ? "is-passed" : ""}
                    style={{
                      left: `${(Math.min(stop.index, totalSegments) / totalSegments) * 100}%`,
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="fa-timeline-speed">
              {SPEEDS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={speed === option ? "is-active" : ""}
                  onClick={() => setSpeed(option)}
                >
                  {option}x
                </button>
              ))}
            </div>
          </div>

          <div className="fa-timeline-meta">
            <span>Start · {formatTime(startTime, "hh:mm A")}</span>
            <span className="fa-timeline-meta-current">
              {formatTime(getPointTime(currentPoint), "DD MMM YYYY, hh:mm A")}
            </span>
            <span>End · {formatTime(endTime, "hh:mm A")}</span>
          </div>

          {stops.length > 0 && (
            <div className="fa-timeline-stops">
              {stops.map((stop, order) => (
                <button
                  type="button"
                  key={`stop-${stop.index}`}
                  className={`fa-timeline-stop ${
                    stop.index <= currentIndex ? "is-passed" : ""
                  }`}
                  onClick={() =>
                    applyProgress(Math.min(stop.index, totalSegments))
                  }
                  title={stop.point?.summary || "Location point"}
                >
                  <span className="fa-timeline-stop-index">{order + 1}</span>
                  <span className="fa-timeline-stop-body">
                    <span className="fa-timeline-stop-time">
                      {formatTime(getPointTime(stop.point), "hh:mm A")}
                    </span>
                    {stop.point?.summary && (
                      <span className="fa-timeline-stop-text">
                        {stop.point.summary.replace(/<[^>]+>/g, "")}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentMapWithPopup;
